import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/homeTheme.css';
import { InteractiveContactForm } from './InteractiveContactForm';
import { Footer } from './Footer';
import { JsonLd } from './Seo';
import { scrollToSection } from '../lib/scrollToSection';
import { goHome } from '../lib/goHome';
import { HomeDashboard } from './HomeDashboard';
import { BookingBanner } from './BookingBanner';
import { CtaLink, CTA_LABEL } from './auraos/walkthrough';
import { AI_STAFF, STAFF_CATEGORIES, TONES, staffImg } from '../data/aiStaff';
import '../styles/homeAuraos.css';

/* ============================================================================
   HomeThemed — the light/professional "autothinklogicflow" theme applied to
   the BRANDCENTRAL home page. Purely cosmetic: every SEO/JSON-LD block still
   lives in HomePage.tsx, the contact form keeps its exact backend wiring
   (the AIOS contact-lead form via InteractiveContactForm), the
   footer + chat widget are untouched, and all section anchor IDs
   (#about #proof #services #process #contact #faqs) are preserved so every
   existing cross-page anchor link + nav keeps working.
   ============================================================================ */

const ArrowBadge = () => (
  <svg className="ht-arw" viewBox="0 0 100 100" aria-hidden="true">
    <g>
      <line x1="34" y1="66" x2="66" y2="34" />
      <polyline points="40,34 66,34 66,60" />
    </g>
  </svg>
);

// ── Card-showcase media — self-hosted on Vercel's CDN (public/img/home,
//    public/video; pulled in by scripts/import-images.mjs) ──
const CARDS = [
  {
    id: 'ht-c1',
    poster:
      '/img/home/card-1-poster.webp',
    src: '/video/home-card-1.mp4',
    lift: false,
    num: '2.8×',
    numId: 'ht-n1',
    sub: 'Average efficiency against the manual baseline, from systems that keep learning.',
    subId: 'ht-s1',
    badgeId: 'ht-b1',
  },
  {
    id: 'ht-c2',
    poster:
      '/img/home/card-2-poster.webp',
    src: '/video/home-card-2.mp4',
    lift: false,
    num: '360+',
    numId: 'ht-n2',
    sub: 'Workflows automated across live client operations, without the busywork.',
    subId: 'ht-s2',
    badgeId: 'ht-b2',
  },
  {
    id: 'ht-c3',
    poster:
      '/img/home/card-3-poster.webp',
    src: '/video/home-card-3.mp4',
    lift: true,
    num: '65+',
    numId: 'ht-n3',
    sub: 'AI Staff deployed across our clients\u2019 businesses, monitored 24/7.',
    subId: 'ht-s3',
    badgeId: 'ht-b3',
  },
];

// ── "Active agents" rows — replaces the mockup's fake team roster so we
//    don't fabricate staff or depend on missing avatar images. ──
const AGENTS = [
  { initials: 'LR', color: 'linear-gradient(135deg,#fbb18a,#ef7a52)', name: 'Lead Router', role: 'Routing', hl: false },
  { initials: 'CD', color: 'linear-gradient(135deg,#a9b6ec,#6f7fd6)', name: 'Content Drafter', role: 'Drafting', hl: true },
  { initials: 'IA', color: 'linear-gradient(135deg,#8fd9a0,#3ecf4c)', name: 'Insight Analyst', role: 'Analyzing', hl: false },
  { initials: 'OM', color: 'linear-gradient(135deg,#e9b6cf,#cf6fa0)', name: 'Ops Monitor', role: 'Monitoring', hl: false },
];

const SERVICES = [
  {
    price: '$800/mo', priceSuffix: 'single agent · full system starting at $2,500/mo', title: 'AuraOS',
    description: 'AI Staff that run your back office, with every draft waiting for your approval. Your web, apps and operations run through it as one system.',
    bullets: ['40+ roles to choose from, plus custom roles', 'Nothing goes out until you say so', 'Content Studio: your videos edited, your posts written'],
    to: '/auraos', linkLabel: 'Explore AuraOS',
    reveal: 'Inside AuraOS: your AI Staff, approvals, content calendar and Content Studio in one place.',
  },
  {
    price: '$450', priceSuffix: 'per page', title: 'Web Design, Re-skins & Hosting',
    description: 'Built on our stack and run through AuraOS, or designed, re-skinned or hosted on Webflow, Framer, Shopify or Next.js.',
    bullets: ['$450 / page, 1 to 50+ pages', 'Optional content + on-page SEO (+$75/page)', 'Full management: hosting, updates, SEO, reporting, audits, performance, security and more, from $2,000/mo'],
    to: '/web-design', linkLabel: 'Explore Web Design',
    reveal: 'Inside AuraOS: hosting, updates, SEO and reports, managed for you.',
  },
  {
    price: '$3,000+', priceSuffix: 'starting at', title: 'App Development',
    description: 'Custom web and mobile apps, run through AuraOS after launch.',
    bullets: ['Web & mobile app development', 'Cross-platform solutions', 'Scalable architecture & deployment'],
    to: '/app-development', linkLabel: 'Explore App Development',
    reveal: 'Inside AuraOS: your app\u2019s requests and updates go through the same approval queue.',
  },
  {
    price: '$497', priceSuffix: 'per 1-hour call', title: 'Operations Consulting',
    description: 'Start free with the 15-minute walkthrough. Go deeper with the 1-hour call and a written blueprint.',
    bullets: ['Workflow analysis & optimization', 'Technology stack review', 'Custom automation roadmap'],
    to: '/operations-consulting', linkLabel: 'Explore Operations Consulting',
    reveal: 'Inside AuraOS: the blueprint becomes the AI Staff you turn on.',
  },
];

const STEPS = [
  { n: '01', title: 'Walkthrough', p: 'Fifteen minutes on where your processes slip and which AI Staff would run them.' },
  { n: '02', title: 'Blueprint', p: 'We map the roles, the tools they connect to and what each one drafts for you.' },
  { n: '03', title: 'Build', p: 'We turn on your AI Staff and connect your site, inbox and calendar. Nothing ships until you approve it.' },
  { n: '04', title: 'Run', p: 'AuraOS runs it day to day. Every draft waits in your queue, and every correction teaches it.' },
];

const NEEDS = [
  { n: '01', title: 'Decisions take too long', p: 'Approvals sit in inboxes and threads. In AuraOS, every draft lands in one queue with the reasoning attached.' },
  { n: '02', title: 'Your systems don\u2019t talk', p: 'Your site, CRM, calendar and inbox each hold part of the picture. AuraOS connects them so your AI Staff work from the same facts.' },
  { n: '03', title: 'Growth adds manual work', p: 'More customers means more follow-ups, invoices and posts that go out when someone finds time. In the Content Studio, your recordings come back edited and your posts come back written, ready to approve.' },
];

const FAQS = [
  { q: 'What does BRANDCENTRAL actually do?', a: 'We run businesses on AuraOS, our own software: AI Staff that each work a role, with every draft waiting for your approval. Websites, apps, AI Staff and operations consulting all run through it. If you do not want AuraOS full management, we also design, re-skin or host your site on Webflow, Framer, Shopify or Next.js.' },
  { q: 'What is AuraOS?', a: 'AuraOS is our AI Operating Foundation. You choose from 40+ roles (plus custom roles), each AI Staff member drafts the work for its role, and nothing goes out until you approve it. Your dashboard shows the AI Staff you turn on. One AI Staff member is $800/mo; the full system starts at $2,500/mo.' },
  { q: 'Do you build your own products, or just client work?', a: 'Both. Our Studio ships software we built because nothing else fit. The flagship is AuraOS (our AI Operating Foundation, an AI staff that files its work for your approval), alongside LegalLio (AI-drafted legal documents for founders, developers, and small businesses) and BRANDCENTRAL CRM (a CRM with AI agents and automation built for digital agencies). Every Studio product started as a client problem we couldn’t solve with existing tools. See the lineup at /studio.' },
  { q: 'How does Web Design pricing work?', a: 'Flat $450 per page, 1–50+ pages, no surprise invoices. Optional add-ons run +$75 per page each: "I need help with page content" (we write it for you) and "I want to optimize for SEO" (technical + on-page SEO baked in). The form on this page calculates a live total against premium and mid-tier agency benchmarks so you can see the savings before you submit.' },
  { q: 'What is included with a Web Design build?', a: 'Production-grade Vite + React (or Webflow, Framer, Next.js, Astro on request), responsive across mobile / tablet / desktop, accessibility checks, performance tuning, on-page SEO with schema markup, and a clean handoff so your team can update copy without us. Local SEO for Mooresville, NC and the Charlotte metro is included whenever the SEO add-on is selected.' },
  { q: 'How long does a typical engagement take?', a: 'The initial audit + blueprint runs 2–3 weeks. Web-design builds typically ship in 2–6 weeks depending on page count and add-ons; ops/agent build phases are scoped to 4–8 week sprints. Most clients see their first shipped improvement inside 30 days.' },
  { q: 'What is the $497 consulting call for?', a: 'A 1-hour working session where we walk through your current workflows, identify the three highest-leverage bottlenecks, and hand you a written blueprint you can act on, even if you never hire us again.' },
  { q: 'Can it post for me?', a: 'Yes. The Content Studio edits your videos and writes your posts and LinkedIn carousels, and once we connect your accounts, what you approve goes out on its schedule: videos to YouTube, Instagram and TikTok, posts to LinkedIn and the other main platforms. Nothing goes out until you approve it. Long talks and podcasts up to 3 hours can be cut into up to 12 Shorts, and a shared Google Drive or Dropbox folder can feed it new recordings automatically.' },
  { q: 'Can you integrate with our existing stack?', a: 'Yes. AuraOS connects to the tools you already use, and we host and manage sites on Webflow, Framer, Shopify or Next.js. Full management includes hosting, updates, SEO, reporting, audits, performance, security and more.' },
  { q: 'What happens after the agents are live?', a: 'They work inside AuraOS. Every draft lands in your approval queue with its reasoning, your corrections teach them, and you can add or pause AI Staff anytime. We watch it alongside you.' },
];

const FAM_NOTES: Record<string, string> = {
  'Content & Growth': 'Runs the Content Studio: videos edited, Shorts cut from long ones, posts and carousels written, all waiting for your approval.',
  'Brand & Creative': 'Designs and video for the Content Studio: images, thumbnails and Shorts in your look.',
};

const EXPLORE = [
  { to: '/ai-agency', label: 'AI Agency Overview' },
  { to: '/intelligent-agents', label: 'AI Agent Development' },
  { to: '/operations-consulting', label: 'Operations Consulting' },
  { to: '/web-design', label: 'Web Design' },
  { to: '/studio', label: 'Studio Products' },
  { to: '/blog', label: 'Field Notes Blog' },
  { to: '/resources', label: 'Free Resources' },
  { to: '/about', label: 'About Samuel' },
];

export const HomeThemed = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [fam, setFam] = useState<string | null>(null);
  const [openStep, setOpenStep] = useState<number | null>(0);
  const faces = (fam ? AI_STAFF.filter((m) => m.category === fam) : AI_STAFF).slice(0, 8);
  const stageRef = useRef<HTMLDivElement>(null);
  const cardsSectionRef = useRef<HTMLElement>(null);

  // Anchor navigation — same helper the rest of the site uses (native
  // smooth scroll). If the target isn't mounted (shouldn't happen on home)
  // it falls through gracefully.
  const go = (target: string) => {
    setMenuOpen(false);
    scrollToSection(target);
  };
  const brandHome = () => {
    setMenuOpen(false);
    goHome(location.pathname, navigate);
  };

  // Lock body scroll while the mobile menu is open; close on Escape / resize
  // to desktop.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    const onResize = () => { if (window.innerWidth > 1024) setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [menuOpen]);

  // Fit the fixed 1672×941 card stage into the viewport (ported from the
  // mockup). On the mobile fallback the stage becomes position:relative and
  // we clear the scale so it flows as a column.
  useEffect(() => {
    const stage = stageRef.current;
    const sec = cardsSectionRef.current;
    if (!stage || !sec) return;
    const fit = () => {
      if (getComputedStyle(stage).position === 'relative') {
        stage.style.removeProperty('--s');
        return;
      }
      const r = sec.getBoundingClientRect();
      const s = Math.min(r.width / stage.offsetWidth, r.height / stage.offsetHeight);
      stage.style.setProperty('--s', String(s));
    };
    fit();
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    let ro: ResizeObserver | undefined;
    if ('ResizeObserver' in window) { ro = new ResizeObserver(fit); ro.observe(sec); }
    return () => {
      window.removeEventListener('resize', fit);
      window.removeEventListener('orientationchange', fit);
      ro?.disconnect();
    };
  }, []);

  // Only play the (heavy) showcase videos while the cards section is on
  // screen — decoding three looping videos non-stop pegs the GPU and makes
  // scrolling stutter across the whole page, especially on mobile. Paused
  // off-screen, the rest of the page scrolls clean; the posters still show.
  useEffect(() => {
    const sec = cardsSectionRef.current;
    if (!sec || typeof IntersectionObserver === 'undefined') return;
    const vids = Array.from(sec.querySelectorAll('video'));
    if (!vids.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        const onScreen = entries[0]?.isIntersecting;
        vids.forEach((v) => {
          if (onScreen) {
            void v.play?.().catch(() => {});
          } else {
            v.pause?.();
          }
        });
      },
      { threshold: 0.01 },
    );
    io.observe(sec);
    return () => io.disconnect();
  }, []);

  return (
    <>
    <div className="ht-root">
      {/* FAQ structured data — preserves the FAQPage schema the old dark
          FAQ section emitted. */}
      <JsonLd
        id="faq-home"
        data={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />

      {/* ── Section one: hero + dashboard ─────────────────────────────── */}
      <section className="ht-one" aria-label="BRANDCENTRAL: AI agency, operations, web, and apps">
        <div className="ht-page">
          <div className="ht-wrap">
            <nav className="ht-nav">
              <div className="ht-nav-inner">
              <a className="ht-brand" href="/" onClick={(e) => { e.preventDefault(); brandHome(); }} aria-label="BRANDCENTRAL home">
                <span className="ht-logo ht-keycap">
                  {/* Command-key monogram. The .ht-key-top group depresses on
                      :active so the mark presses down like a real key. */}
                  <svg viewBox="0 0 64 64" fill="none" role="img" aria-label="BRANDCENTRAL">
                    <rect x="8" y="9" width="48" height="48" rx="14" fill="#e9e7f0" stroke="#141414" strokeWidth="3" />
                    <g className="ht-key-top">
                      <rect x="8" y="9" width="48" height="42" rx="14" fill="#fff" stroke="#141414" strokeWidth="3" />
                      <rect x="14" y="15" width="36" height="30" rx="8" fill="none" stroke="#141414" strokeWidth="1.4" opacity="0.28" />
                      <text x="32" y="30.5" fontFamily='"Hanken Grotesk", system-ui, sans-serif' fontSize="19" fontWeight="700" fill="#141414" textAnchor="middle" dominantBaseline="central" letterSpacing="-1.2">BC</text>
                    </g>
                  </svg>
                </span>
                <span className="ht-wordmark">BRANDCENTRAL</span>
              </a>
              <div className="ht-nav-links">
                <a href="/auraos" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/auraos'); }}>AuraOS</a>
                <a href="/#about" onClick={(e) => { e.preventDefault(); go('#about'); }}>About</a>
                <a href="/#services" onClick={(e) => { e.preventDefault(); go('#services'); }}>Services</a>
                <a href="/#process" onClick={(e) => { e.preventDefault(); go('#process'); }}>Process</a>
                <a href="/#faqs" onClick={(e) => { e.preventDefault(); go('#faqs'); }}>FAQ</a>
                <a href="/blog" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/blog'); }}>Blog</a>
              </div>
              <CtaLink source="home-nav" className="ht-btn ht-btn-nav">{CTA_LABEL}</CtaLink>
              <button className={`ht-burger${menuOpen ? ' open' : ''}`} aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)}>
                <span /><span /><span />
              </button>
              </div>
            </nav>

            <div className={`ht-menu-backdrop${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(false)} />
            <div className={`ht-menu-panel${menuOpen ? ' open' : ''}`} aria-hidden={!menuOpen}>
              <a href="/auraos" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/auraos'); }}>AuraOS</a>
              <a href="/#about" onClick={(e) => { e.preventDefault(); go('#about'); }}>About</a>
              <a href="/#services" onClick={(e) => { e.preventDefault(); go('#services'); }}>Services</a>
              <a href="/#process" onClick={(e) => { e.preventDefault(); go('#process'); }}>Process</a>
              <a href="/#faqs" onClick={(e) => { e.preventDefault(); go('#faqs'); }}>FAQ</a>
              <a href="/blog" onClick={(e) => { e.preventDefault(); setMenuOpen(false); navigate('/blog'); }}>Blog</a>
              <div className="ht-menu-div" />
              <CtaLink source="home-nav" className="ht-btn ht-menu-cta">{CTA_LABEL}</CtaLink>
            </div>

            <BookingBanner />

            <div className="ht-hero">
              <h1 className="ht-headline">
                <span>Unleash Your</span>
                <span>Full Power</span>
              </h1>
              <div className="ht-hero-right">
                <p className="ht-subtitle" data-speakable="true">
                  AuraOS is our own software, and every service we offer runs
                  through it: AI Staff, websites, apps and operations. Want to
                  stay on your platform? We also design, re-skin or host on
                  Webflow, Framer, Shopify or Next.js.
                </p>
                <CtaLink source="home-hero" className="ht-btn ht-btn-hero">{CTA_LABEL}</CtaLink>
                <a className="ht-herolink" href="/#services" onClick={(e) => { e.preventDefault(); go('#services'); }}>See What We Build</a>
                <p className="ht-geo">AI Agency · Mooresville, NC · Lake Norman · Charlotte</p>
              </div>
            </div>

            {/* Sample AuraOS portal: autoplays and is clickable; three sample setups. */}
            <div className="ht-showcase">
              <HomeDashboard />
            </div>
          </div>
        </div>
      </section>

      {/* ── Section two: proof card showcase (videos kept) ───────────────── */}
      <section id="proof" className="ht-two" ref={cardsSectionRef} aria-label="Proof: what the systems deliver">
        <div className="ht-viewport">
          <div className="ht-stage" ref={stageRef}>
            <h2 className="ht-title"><span>Automate the complexity.</span><span>Keep the control.</span></h2>
            {CARDS.map((c) => (
              <div className="ht-card" id={c.id} key={c.id}>
                <video className="ht-card-media" muted loop playsInline preload="none" aria-hidden="true" poster={c.poster}>
                  <source src={c.src} />
                </video>
                {c.lift && <div className="ht-lift" />}
                <div className="ht-num" id={c.numId}>{c.num}</div>
                <div className="ht-cardsub" id={c.subId}>{c.sub}</div>
                <div className="ht-badge" id={c.badgeId}><ArrowBadge /></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About / leverage (dark) ──────────────────────────────────────── */}
      <section id="about" className="ht-journey">
        <div className="ht-inner">
          <p className="ht-eyebrow">Where AI creates leverage</p>
          <h2>You don’t need more tools. You need the right system.</h2>
          <p className="ht-journey-lead">
            Processes are the structure of your business. When they are
            missing, outdated or nobody has time to run them, things slip.
            AuraOS gives each process an AI Staff member who runs it, and you
            approve the work.
          </p>
          <div className="ht-needs">
            {NEEDS.map((n) => (
              <article className="ht-need" key={n.n}>
                <span className="ht-need-index">{n.n}</span>
                <h3>{n.title}</h3>
                <p>{n.p}</p>
              </article>
            ))}
          </div>
          <CtaLink source="home-process" className="ht-cta-pill">{CTA_LABEL}</CtaLink>
        </div>
      </section>

      {/* ── Services (light) ─────────────────────────────────────────────── */}
      <section id="services" className="ht-services">
        <div className="ht-inner">
          <div className="ht-services-head">
            <h2>Four ways we put AI to work.</h2>
            <p>All our services run through AuraOS, our own software. We will also design a site, re-skin an existing site, or host it on the platform you like if you do not want AuraOS full management.</p>
          </div>
          <div className="ht-svc-grid">
            {SERVICES.map((s) => (
              <a className="ht-svc-card" href={s.to} key={s.title}>
                <div className="ht-svc-price"><b>{s.price}</b><span>{s.priceSuffix}</span></div>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
                <ul className="ht-svc-bullets">{s.bullets.map((b) => <li key={b}>{b}</li>)}</ul>
                <span className="ht-svc-reveal">{s.reveal}</span>
                <span className="ht-svc-link">{s.linkLabel}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── AuraOS feature band (dark, the flagship) ───────────────────────── */}
      <section id="aios" className="ht-aios" aria-label="AuraOS: the AI Operating Foundation">
        <div className="ht-aios-inner">
          <p className="ht-aios-eyebrow">AuraOS: The AI Operating Foundation</p>
          <h2>Your back office, staffed by AI. <span className="aa-grad">Nothing ships without you.</span></h2>
          <p className="ht-aios-lead">
            AuraOS runs your back office. AI staff each work a role (leads, content, onboarding, support, invoicing) and everything they produce lands in an approval queue. Nothing reaches the outside world until you say go.
          </p>
          <ul className="ht-aios-pills">
            <li>You stay in control</li>
            <li>Learns your business, not the internet&rsquo;s</li>
            <li>Hire the roles you could never justify</li>
          </ul>
          <div className="ht-aios-roles">
            <p className="ht-aios-roles-lead">
              40+ roles to choose from across 8 business functions, plus a custom role when you need one. 65+ AI Staff deployed across our clients&rsquo; businesses.
            </p>
            <ul className="ht-aios-fams" role="group" aria-label="Filter AI Staff by function">
              {STAFF_CATEGORIES.map((c) => (
                <li key={c}>
                  <button type="button" className={`ht-fam${fam === c ? ' on' : ''}`} aria-pressed={fam === c} onClick={() => setFam(fam === c ? null : c)}>{c}</button>
                </li>
              ))}
              <li className="custom">+ Your custom role</li>
            </ul>
            {fam && FAM_NOTES[fam] && <p className="ht-fam-note">{FAM_NOTES[fam]}</p>}
            <ul className="ht-aios-faces" aria-label={fam ? `${fam} AI Staff` : 'AI Staff from the catalog'}>
              {faces.map((m) => (
                <li key={m.slug}>
                  <span className="ht-face" style={{ background: TONES[m.tone].soft }}>
                    <img src={staffImg(m.image, 96)} alt="" width={44} height={44} loading="lazy" decoding="async" draggable={false} />
                  </span>
                  <span className="ht-face-t"><b>{m.name}</b><i>{m.role}</i></span>
                </li>
              ))}
            </ul>
          </div>
          <div className="ht-aios-cta">
            <CtaLink source="home-aios" className="ht-btn ht-btn-hero">{CTA_LABEL}</CtaLink>
            <a className="ht-aios-link" href="/auraos" onClick={(e) => { e.preventDefault(); navigate('/auraos'); }}>See the full system →</a>
          </div>
        </div>
      </section>

      {/* ── Process (light) ──────────────────────────────────────────────── */}
      <section id="process" className="ht-process">
        <div className="ht-inner">
          <div className="ht-process-head">
            <div>
              <p className="ht-eyebrow" style={{ color: '#9a9b9e' }}>A clear path to value</p>
              <h2>Built around your business, not an AI demo.</h2>
            </div>
            <p>We start with the bottleneck, prove the value, and build a reliable system your team can understand and operate.</p>
          </div>
          <div className="ht-steps">
            {STEPS.map((st, i) => {
              const open = openStep === i;
              return (
                <article className={`ht-step${open ? ' open' : ''}`} key={st.n}>
                  <span>{st.n}</span>
                  <h3><button type="button" className="ht-step-btn" aria-expanded={open} onClick={() => setOpenStep(open ? null : i)}>{st.title}<i aria-hidden="true" /></button></h3>
                  <p>{st.p}</p>
                </article>
              );
            })}
          </div>
          <CtaLink source="home-process" className="ht-cta-pill ht-cta-dark">{CTA_LABEL}</CtaLink>
        </div>
      </section>

      {/* ── Contact (dark band — hosts the existing form untouched) ──────── */}
      <section id="contact" className="ht-contact">
        <div className="ht-contact-inner">
          <div className="ht-contact-copy">
            <p className="ht-eyebrow">Your next best step</p>
            <h2>Let’s find where AI can create real leverage.</h2>
            <p>
              Start with a free 15-minute walkthrough. We look at where your
              processes slip and which AI Staff would run them, and you leave
              with clear next steps.
            </p>
            <div className="ht-trust">
              <span>15 minutes, free</span>
              <span>Clear, practical next steps</span>
              <span>No hard sell</span>
            </div>
            <div style={{ marginTop: 28 }}>
              <CtaLink source="home-contact" className="ht-btn ht-btn-hero">{CTA_LABEL}</CtaLink>
            </div>
          </div>
          <div className="ht-form-host">
            <InteractiveContactForm variant="light" />
          </div>
        </div>
      </section>

      {/* ── FAQ (light) ──────────────────────────────────────────────────── */}
      <section id="faqs" className="ht-faq">
        <div className="ht-inner">
          <p className="ht-eyebrow" style={{ color: '#9a9b9e' }}>Answers</p>
          <h2>Questions, answered.</h2>
          <div className="ht-faq-list">
            {FAQS.map((f, i) => {
              const open = openFaq === i;
              return (
                <div className={`ht-faq-item${open ? ' open' : ''}`} key={f.q}>
                  <button className="ht-faq-q" aria-expanded={open} onClick={() => setOpenFaq(open ? null : i)}>
                    {f.q}
                    <span className="ht-faq-icon" aria-hidden="true" />
                  </button>
                  <div className="ht-faq-a" role="region">
                    <div className="ht-faq-a-inner">{f.a}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Explore priority nav (SEO internal-linking surface) ──────────── */}
      <nav className="ht-explore" aria-label="Primary site sections">
        <p className="ht-eyebrow">Explore BRANDCENTRAL</p>
        <ul>
          {EXPLORE.map((e) => (
            <li key={e.to}><a href={e.to}>{e.label}</a></li>
          ))}
        </ul>
      </nav>
    </div>
    <Footer />
    </>
  );
};
